import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthShell } from '../components/auth/AuthShell';
import {
  AUTH_LOADING_SHELL_CLASS,
  AUTH_LOADING_SHELL_STYLE,
  AuthLoadingIndicator,
} from '../components/auth/AuthLoadingIndicator';
import {
  AuthenticateWithRedirectCallback,
  useClerk,
  useSignIn,
  useSignUp,
} from '../lib/auth';
import { safeRedirect } from '../lib/utils';
import { capture } from '../lib/analytics';
import { failDashboardOAuthAttempt } from '../lib/dashboardAuthTelemetry';
import { clearPendingCallback, sessionStorageOrNull, writePendingCallback } from '../lib/authCallbackPending';
import { log } from '@/lib/logger';
import {
  resolveAuthCallbackOutcome,
  AUTH_CALLBACK_POLL_INTERVAL_MS,
  nextAuthCallbackEntry,
  isAuthCallbackLoop,
  withGoogleUnfinishedFlag,
  type AuthCallbackEntryState,
} from './auth-callback-outcome';

/* eslint-disable @typescript-eslint/no-explicit-any */

// Persisted across re-mounts (same tab) so the breaker can see a redirect loop
// the single-mount deadline can't. Cleared on success and on manual retry.
const AUTH_CALLBACK_ENTRY_STORAGE_KEY = 'oqa_auth_cb_entries';

function clearAuthCallbackEntries(): void {
  try {
    sessionStorage.removeItem(AUTH_CALLBACK_ENTRY_STORAGE_KEY);
  } catch {
    // sessionStorage unavailable (private mode / blocked) — nothing to clear.
  }
}

export function AuthCallbackPage() {
  const clerk = useClerk() as any;
  const { signUp } = useSignUp();
  const { signIn } = useSignIn();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const entered = useRef(false);
  const mountTs = useRef(Date.now());
  const [readyForCallback, setReadyForCallback] = useState(false);
  const [looped, setLooped] = useState(false);
  const [verificationPending, setVerificationPending] = useState(false);

  const su = signUp as any;
  const si = signIn as any;

  // Three-tier redirect resolution: signUp metadata > signIn metadata >
  // URL ?redirect_url= > default /dashboard. Used by /beta/:slug flow.
  const redirectTarget =
    safeRedirect(su?.unsafeMetadata?.redirectUrl as string | null)
    || safeRedirect(si?.unsafeMetadata?.redirectUrl as string | null)
    || safeRedirect(params.get('redirect_url'))
    || '/dashboard';

  // Keep the deep link across any bounce to /sign-in: SignInPage reads
  // ?redirect_url= (through safeRedirect) and redirects there once a session
  // hydrates, so a failed callback no longer strands the user on /dashboard.
  const signInBounceUrl =
    redirectTarget !== '/dashboard'
      ? `/sign-in?redirect_url=${encodeURIComponent(redirectTarget)}`
      : '/sign-in';

  useEffect(() => {
    if (entered.current) return;
    const isLoaded = clerk?.loaded ?? clerk?.client;
    if (!isLoaded) return;
    entered.current = true;

    const diag = buildDiagnostics({ clerk, su, si });

    // PRIMARY TELEMETRY — fire synchronously on mount. AWRC navigates away
    // within ~500ms and unmounts this component, so anything we defer here
    // (timer, await, etc.) won't reliably ship. This event guarantees we
    // capture the entry-time diagnostic payload for every callback visit.
    capture('auth_callback_entered', {
      method: 'oauth',
      flow: 'builtin_component',
      ...diag,
    });

    // The completed event is reported by the LANDING page, not here. clerk-js
    // 5.128.0 setActive clears the session into a transitive state, awaits a
    // full-page navigate, and returns while unloading before it ever emits the
    // hydrated session, so neither the poll nor the listener below can see
    // success (PostHog 2026-09-05..10-05: 46 landings, 2 completed events).
    // Leave a note; ProtectedRoute consumes it once Clerk hydrates signed-in.
    // Every outcome this page CAN observe clears it, so nothing double-reports.
    writePendingCallback(sessionStorageOrNull(), {
      entered_at: Date.now(),
      redirect_target: redirectTarget,
      google_attempt: diag.referrer_origin === 'https://accounts.google.com'
        || clerk?.client?.signIn?.firstFactorVerification?.strategy === 'oauth_google'
        || clerk?.client?.signUp?.verifications?.externalAccount?.strategy === 'oauth_google',
    });

    // Circuit breaker for the continueSignUpUrl="/auth/callback" self-loop: an
    // unsatisfiable sign-up requirement (e.g. a Clerk-required field OAuth can't
    // supply) makes AWRC re-navigate here indefinitely. Count re-entries across
    // mounts and abort once it's clearly looping, so a misconfiguration fails
    // fast with a clear message instead of pinning the user in a redirect loop.
    // The per-mount deadline below can't see this — it resets every mount.
    try {
      const raw = sessionStorage.getItem(AUTH_CALLBACK_ENTRY_STORAGE_KEY);
      const prev = raw ? (JSON.parse(raw) as AuthCallbackEntryState) : null;
      const entry = nextAuthCallbackEntry(prev, Date.now());
      sessionStorage.setItem(AUTH_CALLBACK_ENTRY_STORAGE_KEY, JSON.stringify(entry));
      if (isAuthCallbackLoop(entry)) {
        failDashboardOAuthAttempt('callback_loop');
        capture('auth_callback_loop_aborted', {
          method: 'oauth',
          flow: 'builtin_component',
          entry_count: entry.count,
          ...diag,
        });
        log.error('[AuthCallback] Re-entry loop detected; aborting to break an infinite redirect loop', {
          entryCount: entry.count,
        });
        setLooped(true);
        clearPendingCallback(sessionStorageOrNull());
        return; // do NOT mount AWRC (it would re-loop) or start the poll
      }
    } catch {
      // sessionStorage unavailable (private mode / blocked) — skip the breaker;
      // the per-mount deadline still bounds each individual attempt.
    }

    // legal_accepted auto-fill MUST settle before AWRC mounts, otherwise
    // AWRC sees stale signUp.status='missing_requirements' and bails. Gate
    // AWRC behind readyForCallback so it doesn't race the update.
    const needsLegalAccepted =
      su?.status === 'missing_requirements'
      && Array.isArray(su.missingFields)
      && su.missingFields.includes('legal_accepted');

    if (needsLegalAccepted) {
      diag.legal_update_attempted = true;
      Promise.resolve(su.update({ legalAccepted: true }))
        .catch((err: unknown) => {
          diag.legal_update_error = (err as { name?: string })?.name || 'Error';
          log.error('[AuthCallback] Failed to set legalAccepted', {
            error: err instanceof Error ? err.message : String(err),
          });
        })
        .finally(() => setReadyForCallback(true));
    } else {
      setReadyForCallback(true);
    }

    // Secondary telemetry + anti-spinner safety net. AWRC normally navigates
    // away (-> /dashboard on success, -> /sign-in on a recoverable state) and
    // unmounts us, which clears this poll. We POLL for the session rather than
    // checking once: a slow-but-successful handshake must not be treated as a
    // failure and bounced to /sign-in. That bounce produced a visible flash of
    // the sign-in form plus a false auth_callback_failed, then an auto-redirect
    // to /dashboard once the session finally settled. Report success the
    // instant a session appears; only declare failure after the deadline.
    let settled = false;
    let previousCheck = Date.now();
    let challengeWaitMs = 0;
    let previousChallengeVisible = false;
    const evaluate = (listenerSession?: unknown) => {
      if (settled) return;
      const sessionPresent = Boolean(listenerSession ?? clerk?.session);
      const now = Date.now();
      const captcha = document.getElementById('clerk-captcha');
      const challengeVisible = Boolean(captcha?.childElementCount
        && captcha.getBoundingClientRect().height > 0
        && getComputedStyle(captcha).visibility !== 'hidden');
      if (previousChallengeVisible || challengeVisible) challengeWaitMs += now - previousCheck;
      previousCheck = now;
      previousChallengeVisible = challengeVisible;
      setVerificationPending(challengeVisible);
      const elapsedMs = now - mountTs.current;
      // A handshake can still complete if Clerk saw OAuth params at entry, has an
      // in-flight sign-in/sign-up, or is already forming a session. If none are
      // ever true this is a bare cold-landing with nothing to finish, so we fail
      // fast instead of pinning the user to the spinner for the full deadline.
      const handshakeInFlight =
        diag.entry_has_any_clerk_anywhere === true
        || Boolean(clerk?.client?.signIn?.status)
        || Boolean(clerk?.client?.signUp?.status)
        || (clerk?.client?.sessions?.length ?? 0) > 0;
      const outcome = resolveAuthCallbackOutcome({
        sessionPresent,
        elapsedMs: Math.max(0, elapsedMs - challengeWaitMs),
        handshakeInFlight,
        verificationPending: challengeVisible,
      });
      if (outcome === 'pending') return;
      settled = true;
      clearInterval(poll);

      const finalDiag = {
        ...diag,
        time_to_resolve_ms: elapsedMs,
        verification_wait_ms: challengeWaitMs,
        su_status_final: (clerk?.client?.signUp ?? su)?.status ?? null,
        si_status_final: (clerk?.client?.signIn ?? si)?.status ?? null,
        clerk_session_present_final: sessionPresent,
        handshake_in_flight_final: handshakeInFlight,
        ...readAttemptDiagnostics(clerk),
      };

      if (outcome === 'completed') {
        log.info('[AuthCallback] Session active after settle');
        clearAuthCallbackEntries();
        clearPendingCallback(sessionStorageOrNull());
        capture('auth_callback_completed', {
          method: 'oauth',
          flow: 'builtin_component',
          delivered_from: 'callback',
          ...finalDiag,
        });
        return;
      }

      // A Google attempt that never formed a session gets a reason on /sign-in
      // (SignInPage reads the flag). A bare visit to this URL, with nothing in
      // flight and not arriving from Google, keeps the plain bounce: the notice
      // would name an attempt nobody made.
      // Only the Google button routes here, so an in-flight sign-in counts as a
      // Google one UNLESS Clerk says it is another method: an abandoned email
      // sign-in reached by a typed URL must not be told Google failed.
      const pendingStrategy = clerk?.client?.signIn?.firstFactorVerification?.strategy;
      const pendingIsOtherMethod =
        typeof pendingStrategy === 'string' && !pendingStrategy.startsWith('oauth_');
      const googleAttempt =
        diag.referrer_origin === 'https://accounts.google.com'
        || (handshakeInFlight && !pendingIsOtherMethod);
      const failureBounceUrl = googleAttempt
        ? withGoogleUnfinishedFlag(signInBounceUrl)
        : signInBounceUrl;
      const failurePayload = {
        reason: handshakeInFlight ? 'no_session' : 'cold_landing_no_handshake',
        bounce_notice: googleAttempt,
        ...finalDiag,
      };
      clearPendingCallback(sessionStorageOrNull());
      failDashboardOAuthAttempt(failurePayload.reason);
      capture('auth_callback_failed', failurePayload);
      try {
        console.group(
          '%c[AuthCallback] FAILURE — full diagnostic dump',
          'color:#c00;font-weight:bold',
        );
        console.log('URL:', window.location.href);
        console.log('Referrer:', document.referrer);
        console.log('Payload:', failurePayload);
        console.log('clerk.client:', clerk?.client);
        console.log('clerk.session:', clerk?.session);
        console.log('clerk.client?.signIn:', clerk?.client?.signIn);
        console.log('clerk.client?.signUp:', clerk?.client?.signUp);
        console.log('clerk.client?.sessions:', clerk?.client?.sessions);
        console.groupEnd();
      } catch {
        // devtools console missing in some embedded environments — ignore
      }

      // Defense in depth: AWRC should have navigated by now via signInUrl;
      // if we somehow reached this branch with no session, take the user
      // back to /sign-in ourselves so they don't stare at a spinner forever.
      navigate(failureBounceUrl, { replace: true });
    };

    const poll = setInterval(evaluate, AUTH_CALLBACK_POLL_INTERVAL_MS);

    // The poll alone cannot deliver the success event: AWRC navigates away the
    // moment the session is active — typically between two poll ticks — and
    // unmount clears the poll, so auth_callback_completed never shipped
    // (PostHog 2026-06-11: 0 completed events in 10 days against daily
    // entered events). Clerk's resource listener fires the instant the session
    // hydrates, before navigation; posthog-js flushes its queue on pagehide,
    // so the capture survives a full-page redirect too.
    const unsubscribe = clerk?.addListener?.((resources: { session?: unknown }) => {
      if (resources?.session) evaluate(resources.session);
    });

    return () => {
      clearInterval(poll);
      unsubscribe?.();
    };
    // su / si intentionally omitted: their references update during the OAuth
    // flow which would re-run this effect, cancel the poll, and prevent the
    // entered event from being captured on first load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clerk?.loaded, clerk?.client]);

  if (looped) {
    return (
      <AuthShell>
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-4 text-gray-700">
          <div className="space-y-1">
            <p className="text-base font-semibold text-gray-900">We couldn&rsquo;t finish signing you in</p>
            <p className="text-sm leading-6">
              Something in the sign-up flow couldn&rsquo;t complete automatically. That&rsquo;s on our side, not
              yours. Head back and try again. If it keeps happening, reach out and we&rsquo;ll sort it.
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              clearAuthCallbackEntries();
              navigate(signInBounceUrl, { replace: true });
            }}
            className="btn-primary mt-3"
          >
            <span className="text-cap-trim">Back to sign in</span>
          </button>
        </div>
      </AuthShell>
    );
  }

  // The wait renders the app's loading screen, NOT AuthShell. AuthShell's
  // background is var(--color-primary), and the very next screen a successful
  // sign-in paints is ProtectedRoute's AuthLoadingScreen on
  // var(--color-surface) — so the old card made one sign-in flash a blue panel
  // and a sentence about an OAuth callback before the white spinner. The
  // failure branch above keeps AuthShell: that one is a card with a button, and
  // it is the screen a user is meant to stop on.
  return (
    <main className={AUTH_LOADING_SHELL_CLASS} style={AUTH_LOADING_SHELL_STYLE}>
      {readyForCallback && (
        <AuthenticateWithRedirectCallback
          signInUrl={signInBounceUrl}
          signUpUrl={signInBounceUrl}
          signInFallbackRedirectUrl={redirectTarget}
          signUpFallbackRedirectUrl={redirectTarget}
          continueSignUpUrl="/auth/callback"
          firstFactorUrl={signInBounceUrl}
          secondFactorUrl={signInBounceUrl}
          resetPasswordUrl={signInBounceUrl}
        />
      )}
      <AuthLoadingIndicator>
        {/* Smart CAPTCHA mount: the OAuth transfer sign-up runs on this page and
            Clerk needs a #clerk-captcha element or it logs "Cannot initialize
            Smart CAPTCHA widget" (recurring $exception here 2026-07-13/16) and
            falls back to an invisible widget that blocks users falsely detected
            as bots with no challenge to pass. Empty until Clerk uses it, and
            `empty:hidden` keeps the column's gap-4 from opening a gap under the
            spinner while it is. */}
        <div id="clerk-captcha" className="flex justify-center empty:hidden" />
        {verificationPending && (
          <div className="space-y-3 text-center text-sm text-gray-700">
            <p>Complete the verification above to continue.</p>
            <button type="button" className="btn-secondary" onClick={() => navigate(signInBounceUrl, { replace: true })}>
              <span className="text-cap-trim">Back to sign in</span>
            </button>
          </div>
        )}
      </AuthLoadingIndicator>
    </main>
  );
}

/**
 * What Clerk and the bot-check mount say at the moment the wait ends. Before
 * this, `auth_callback_failed` could only report `si_status_final`, so a sign-in
 * refused by the bot check (Turnstile error 600010 in the console, PostHog
 * 2026-10-06) was indistinguishable from any other missing session. Error CODES
 * only: Clerk's messages can carry the user's email address. The mount's height
 * separates a visible challenge from an invisible or absent one.
 */
function readAttemptDiagnostics(clerk: any): Record<string, unknown> {
  const firstFactor = clerk?.client?.signIn?.firstFactorVerification;
  const externalAccount = clerk?.client?.signUp?.verifications?.externalAccount;
  const captchaMount = document.getElementById('clerk-captcha');
  return {
    si_first_factor_status: firstFactor?.status ?? null,
    si_first_factor_error_code: firstFactor?.error?.code ?? null,
    su_external_account_status: externalAccount?.status ?? null,
    su_external_account_error_code: externalAccount?.error?.code ?? null,
    captcha_mounted: (captchaMount?.childElementCount ?? 0) > 0,
    captcha_height_px: Math.round(captchaMount?.getBoundingClientRect().height ?? 0),
  };
}

interface BuildDiagnosticsInput {
  clerk: any;
  su: any;
  si: any;
}

function buildDiagnostics({ clerk, su, si }: BuildDiagnosticsInput): Record<string, unknown> {
  const entryParams = new URLSearchParams(window.location.search);
  const paramNames = Array.from(entryParams.keys());
  const clerkParamNames = paramNames.filter((k) => k.startsWith('__clerk'));

  let referrerOrigin: string | null = null;
  try {
    referrerOrigin = document.referrer ? new URL(document.referrer).origin : null;
  } catch {
    referrerOrigin = null;
  }

  const rawHash = window.location.hash.startsWith('#') ? window.location.hash.slice(1) : '';
  const hashParams = rawHash ? new URLSearchParams(rawHash) : new URLSearchParams();
  const hashParamNames = Array.from(hashParams.keys());
  const clerkHashParamNames = hashParamNames.filter((k) => k.startsWith('__clerk'));

  // Storage probes — detect third-party cookie / localStorage blocks.
  let cookieWritable = false;
  try {
    const probe = `__oqa_probe_${Date.now()}`;
    document.cookie = `${probe}=1; SameSite=Lax; Path=/`;
    cookieWritable = document.cookie.includes(probe);
    if (cookieWritable) document.cookie = `${probe}=; Max-Age=0; Path=/`;
  } catch {
    cookieWritable = false;
  }
  let localStorageWritable = false;
  try {
    const key = `__oqa_probe_${Date.now()}`;
    localStorage.setItem(key, '1');
    localStorageWritable = localStorage.getItem(key) === '1';
    localStorage.removeItem(key);
  } catch {
    localStorageWritable = false;
  }

  const cookieNames = (() => {
    try {
      return document.cookie
        .split(';')
        .map((c) => c.split('=')[0].trim())
        .filter(Boolean);
    } catch {
      return [];
    }
  })();
  const clerkCookieNames = cookieNames.filter(
    (n) =>
      n.startsWith('__client')
      || n.startsWith('__session')
      || n.startsWith('__clerk')
      || n.startsWith('__refresh'),
  );

  let navigationType: string | null = null;
  let pageLoadDurationMs: number | null = null;
  try {
    const nav = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming | undefined;
    navigationType = nav?.type ?? null;
    pageLoadDurationMs = nav ? Math.round(nav.duration) : null;
  } catch {
    // performance API unavailable in some environments
  }

  return {
    clerk_loaded: Boolean(clerk?.loaded),
    clerk_loaded_fallback_client: !clerk?.loaded && Boolean(clerk?.client),
    clerk_client_present: Boolean(clerk?.client),
    su_present_at_entry: Boolean(su),
    si_present_at_entry: Boolean(si),
    su_status_at_entry: su?.status ?? null,
    si_status_at_entry: si?.status ?? null,
    su_missing_at_entry: Array.isArray(su?.missingFields) && su.missingFields.length > 0
      ? su.missingFields.join(',')
      : null,
    legal_update_attempted: false,
    legal_update_error: null as string | null,
    entry_param_count: paramNames.length,
    entry_clerk_param_names: clerkParamNames.length > 0 ? clerkParamNames.sort().join(',') : null,
    entry_has_clerk_handshake: clerkParamNames.includes('__clerk_handshake'),
    entry_has_clerk_status: clerkParamNames.includes('__clerk_status'),
    entry_has_clerk_db_jwt: clerkParamNames.includes('__clerk_db_jwt'),
    entry_has_redirect_url: entryParams.has('redirect_url'),
    entry_hash_param_count: hashParamNames.length,
    entry_clerk_hash_param_names: clerkHashParamNames.length > 0 ? clerkHashParamNames.sort().join(',') : null,
    entry_has_any_clerk_anywhere: clerkParamNames.length + clerkHashParamNames.length > 0,
    cookie_writable: cookieWritable,
    local_storage_writable: localStorageWritable,
    referrer_origin: referrerOrigin,
    referrer_is_same_origin: referrerOrigin === window.location.origin,
    entry_all_param_names: paramNames.sort().join(',') || null,
    entry_all_hash_param_names: hashParamNames.sort().join(',') || null,
    oauth_error: entryParams.get('error'),
    oauth_error_description: entryParams.get('error_description'),
    cookie_total_count: cookieNames.length,
    clerk_cookie_names: clerkCookieNames.length > 0 ? clerkCookieNames.sort().join(',') : null,
    clerk_cookie_count: clerkCookieNames.length,
    navigation_type: navigationType,
    page_load_duration_ms: pageLoadDurationMs,
    history_length: typeof window !== 'undefined' ? window.history.length : null,
    window_opener_present: typeof window !== 'undefined' ? Boolean(window.opener) : false,
    clerk_client_id_present: Boolean(clerk?.client?.id),
    clerk_sessions_count: clerk?.client?.sessions?.length ?? null,
    clerk_active_sessions_count: clerk?.client?.activeSessions?.length ?? null,
    clerk_user_present: Boolean(clerk?.user),
    clerk_version: (clerk as { version?: string })?.version ?? null,
  };
}
